/* eslint-disable react-hooks/exhaustive-deps */
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { getApiCaller } from "../../libs/apiCaller";
import { useUnauthorizedNavigation } from "../../libs/UnauthorisedPage";

const AddPatient = ({
  isOpen,
  onClose,
  patientId,
  consumerId,
  patientData,
  setPatientData,
  getConsumerDetails,
  handleDelete,
  isPatientDelete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const redirectToLogin = useUnauthorizedNavigation();

  useEffect(() => {
    if (patientId) {
      formik.setValues({
        patientFirstName: patientData?.first_name,
        patientLastName: patientData?.last_name,
        postalCode: patientData?.postal_code,
        street: patientData?.street,
        state: patientData?.state,
        city: patientData?.city,
        apt_suite: patientData?.apt_suite || "",
      });
    }
  }, [patientId, patientData]);

  const formik = useFormik({
    initialValues: {
      patientFirstName: "",
      patientLastName: "",
      postalCode: "",
      street: "",
      state: "",
      city: "",
      apt_suite: "",
    },
    validationSchema: Yup.object({
      patientFirstName: Yup.string()
        .min(2, "Must be at least 2 characters")
        .required("Patient First Name Required"),
      patientLastName: Yup.string()
        .min(2, "Must be at least 2 characters")
        .required("Patient Last Name Required"),
      postalCode: Yup.string().required("Patient Postal Code Required"),
      street: Yup.string().required("Patient Street Required"),
      state: Yup.string().required("Patient State Required"),
      city: Yup.string().required("Patient City Required"),
      apt_suite: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const endPoint = `${patientId ? "edit_patient" : "add_patient"}?${
        patientId ? "patient_id" : "consumer_id"
      }=${patientId ? patientId : consumerId}&first_name=${
        values?.patientFirstName
      }&last_name=${values?.patientLastName}&postal_code=${
        values?.postalCode
      }&street=${values?.street}&state=${values?.state}&city=${
        values?.city
      }&apt_suite=${values.apt_suite}`;

      try {
        const response = await getApiCaller(endPoint);
        if (response?.data?.response_code === 200 || response?.status === 200) {
          setIsLoading(false);
          onClose();
          formik.resetForm();
          toast.success(
            response?.data?.response_message || "Patient Store Successfully"
          );
          getConsumerDetails && getConsumerDetails();
        }
      } catch (error) {
        toast.error("Failed to fetch data.");
      }
      setIsLoading(false);
    },
  });

  return (
    <>
      <Dialog open={isOpen} onClose={() => {}} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-0 sm:p-4">
            <DialogPanel className="relative w-full transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-lg max-w-[95vw] sm:max-w-md md:max-w-lg">
              <div className="bg-[#21140E0F] ">
                <div className="flex justify-between items-center px-3 pt-2 sm:px-6">
                  <DialogTitle className="text-base sm:text-xl font-bold text-[#284184]">
                    {`${patientId ? "Edit" : "Add"} Patient`}
                  </DialogTitle>

                  <button
                    onClick={() => {
                      onClose();
                      formik.resetForm();
                      setPatientData({});
                    }}
                    className="text-[#1C2024] p-2 rounded-full transition-colors"
                  >
                    <span className="text-4xl leading-none">&times;</span>
                  </button>
                </div>

                <form onSubmit={formik.handleSubmit} className="">
                  <div className="h-[70vh] overflow-auto px-3 pb-6 pt-2 sm:px-6">
                    <div>
                      <label className="block mb-2">Patient First Name *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="patientFirstName"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter Patient First Name"
                          {...formik.getFieldProps("patientFirstName")}
                        />
                        {formik.touched.patientFirstName &&
                        formik.errors.patientFirstName ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.patientFirstName}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="my-3">
                      <label className="block mb-2">Patient Last Name *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="patientLastName"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter Patient Last Name"
                          {...formik.getFieldProps("patientLastName")}
                        />
                        {formik.touched.patientLastName &&
                        formik.errors.patientLastName ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.patientLastName}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="my-3">
                      <label className="block mb-2">Street *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="street"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter Street Name"
                          {...formik.getFieldProps("street")}
                        />
                        {formik.touched.street && formik.errors.street ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.street}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="my-3">
                      <label className="block mb-2">Apt/Suite Number </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="apt_suite"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter Apt/Suite Number"
                          {...formik.getFieldProps("apt_suite")}
                        />
                        {formik.touched.apt_suite && formik.errors.apt_suite ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.apt_suite}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="my-3">
                      <label className="block mb-2">City *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="city"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter City"
                          {...formik.getFieldProps("city")}
                        />
                        {formik.touched.city && formik.errors.city ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.city}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="my-3">
                      <label className="block mb-2">State *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="state"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter State"
                          {...formik.getFieldProps("state")}
                        />
                        {formik.touched.state && formik.errors.state ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.state}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="my-3">
                      <label className="block mb-2">Postal Code *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="postalCode"
                          className="pl-6 w-full rounded-xl border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                          placeholder="Enter Postal Code"
                          {...formik.getFieldProps("postalCode")}
                        />
                        {formik.touched.postalCode &&
                        formik.errors.postalCode ? (
                          <div className="text-red-500 text-sm mt-1">
                            {formik.errors.postalCode}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3  px-3 pb-6 pt-2 sm:px-6">
                    {patientId && (
                      <button
                        type="button"
                        onClick={() => handleDelete(patientData?.id)}
                        className="w-full rounded-xl border font-bold border-[#F04438] bg-white px-3 sm:px-4 py-2 text-sm text-[#F04438]"
                        disabled={isPatientDelete}
                      >
                        {isPatientDelete ? "Loading..." : "Delete"}
                      </button>
                    )}
                    <button
                      type="submit"
                      className={`w-full rounded-xl font-bold bg-[#284184] px-3 sm:px-4 py-2 ${
                        patientId ? "text-sm" : "text-xl"
                      } text-white tracking-wider disabled:bg-gray-400 disabled:cursor-not-allowed`}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        "Loading..."
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          {patientId ? (
                            "Save"
                          ) : (
                            <>
                              <FaPlus />
                              Add
                            </>
                          )}
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddPatient;
